import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";

import {
  BadgeEuro,
  Banknote,
  Building2,
  Check,
  ChevronDown,
  CircleDollarSign,
  Edit3,
  FileSliders,
  Filter,
  Globe2,
  Landmark,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  ToggleLeft,
  ToggleRight,
  WalletCards,
  X,
} from "lucide-react";

import { useToast } from "../../../components/feedback/ToastProvider";
import { getCountryName } from "../../../utils/countries";

import "./AdminFundingSourcesPage.css";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const FUNDING_SOURCES_URL = `${API_BASE}/api/admin/funding-source-types/`;
const MARKET_REGIONS_URL = `${API_BASE}/api/admin/market-regions/`;

const CATEGORY_VALUES = ["LOAN", "GRANT", "SUBSIDY", "OWN_FUNDS", "OTHER"];
const ALLOCATION_ROLE_VALUES = ["LENDER", "OSS", "ADMIN"];

const EMPTY_FORM = {
  id: null,
  code: "",
  name: "",
  category: "LOAN",
  market_region_ids: [],
  selectable_by_lender: true,
  is_lender_financing: false,
  can_fund_invoices: true,
  allocation_role: "LENDER",
  allocation_rules: {},
  is_active: true,
  sort_order: 1,
};

/* =========================================================
   HELPERS
   ========================================================= */

function normalizeCode(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function parseApiError(payload) {
  if (!payload) return "Something went wrong.";

  if (typeof payload.detail === "string") {
    const errors = payload.errors;

    if (!errors || typeof errors !== "object") {
      return payload.detail;
    }

    const firstField = Object.keys(errors)[0];

    if (!firstField) {
      return payload.detail;
    }

    const fieldErrors = errors[firstField];

    if (Array.isArray(fieldErrors) && fieldErrors.length) {
      return String(fieldErrors[0]);
    }

    if (typeof fieldErrors === "string") {
      return fieldErrors;
    }

    return payload.detail;
  }

  if (typeof payload.error === "string") {
    return payload.error;
  }

  return "Something went wrong.";
}

function marketRegionLabel(region, language) {
  if (!region) return "";

  const country = region.country_code
    ? getCountryName(region.country_code, language)
    : "";

  return [country, region.name].filter(Boolean).join(" · ");
}

function normalizeRegionIds(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return [
    ...new Set(
      value
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item) && item > 0),
    ),
  ];
}

function getSourceMarkets(source) {
  return Array.isArray(source?.market_regions) ? source.market_regions : [];
}

function Toggle({ checked, onChange, label, description, disabled = false }) {
  return (
    <button
      type="button"
      className={`afs-toggle ${checked ? "is-on" : ""} ${
        disabled ? "is-disabled" : ""
      }`}
      onClick={() => {
        if (!disabled) {
          onChange(!checked);
        }
      }}
      aria-pressed={checked}
      disabled={disabled}
    >
      <span className="afs-toggle__icon">
        {checked ? (
          <ToggleRight size={31} strokeWidth={1.9} />
        ) : (
          <ToggleLeft size={31} strokeWidth={1.9} />
        )}
      </span>

      <span className="afs-toggle__content">
        <strong>{label}</strong>
        {description ? <small>{description}</small> : null}
      </span>
    </button>
  );
}

function StatusBadge({ active, activeLabel, inactiveLabel }) {
  return (
    <span className={`afs-status ${active ? "is-active" : "is-inactive"}`}>
      <span className="afs-status__dot" />
      {active ? activeLabel : inactiveLabel}
    </span>
  );
}

function SummaryCard({ icon: Icon, label, value, hint, delay = 0 }) {
  return (
    <article
      className="afs-summary-card"
      style={{ animationDelay: `${delay}ms` }}
    >
      <span className="afs-summary-card__icon">
        <Icon size={22} strokeWidth={2} />
      </span>

      <div className="afs-summary-card__body">
        <span className="afs-summary-card__label">{label}</span>
        <strong className="afs-summary-card__value">{value}</strong>
        <span className="afs-summary-card__hint">{hint}</span>
      </div>
    </article>
  );
}

/* =========================================================
   PAGE
   ========================================================= */

export default function AdminFundingSourcesPage() {
  const { t, i18n } = useTranslation("admin-funding-sources");
  const { getAccessTokenSilently } = useAuth0();
  const { showToast } = useToast();

  /* =======================================================
     DATA
     ======================================================= */

  const [fundingSources, setFundingSources] = React.useState([]);
  const [marketRegions, setMarketRegions] = React.useState([]);

  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState("");

  /* =======================================================
     FILTERS
     ======================================================= */

  const [search, setSearch] = React.useState("");
  const [countryFilter, setCountryFilter] = React.useState("");
  const [regionFilter, setRegionFilter] = React.useState("");
  const [categoryFilter, setCategoryFilter] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("ALL");

  /* =======================================================
     MODAL
     ======================================================= */

  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalMode, setModalMode] = React.useState("CREATE");

  const [form, setForm] = React.useState(EMPTY_FORM);
  const [initialForm, setInitialForm] = React.useState(EMPTY_FORM);

  const [formErrors, setFormErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [codeTouched, setCodeTouched] = React.useState(false);

  /* =======================================================
     REQUEST
     ======================================================= */

  const apiRequest = React.useCallback(
    async (url, options = {}) => {
      const token = await getAccessTokenSilently();

      const response = await fetch(url, {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          ...(options.body ? { "Content-Type": "application/json" } : {}),
          ...(options.headers || {}),
        },
      });

      let payload = null;

      try {
        payload = await response.json();
      } catch (_error) {
        payload = null;
      }

      if (!response.ok) {
        const requestError = new Error(parseApiError(payload));
        requestError.payload = payload;
        throw requestError;
      }

      return payload;
    },
    [getAccessTokenSilently],
  );

  /* =======================================================
     LOAD
     ======================================================= */

  const loadData = React.useCallback(
    async ({ silent = false } = {}) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const [fundingPayload, regionsPayload] = await Promise.all([
          apiRequest(FUNDING_SOURCES_URL),
          apiRequest(MARKET_REGIONS_URL),
        ]);

        setFundingSources(
          Array.isArray(fundingPayload?.results) ? fundingPayload.results : [],
        );

        setMarketRegions(
          Array.isArray(regionsPayload?.results) ? regionsPayload.results : [],
        );
      } catch (requestError) {
        const message =
          requestError?.message ||
          t("errors.load", "Could not load funding source configuration.");

        setError(message);

        showToast({
          type: "error",
          title: t("toast.loadErrorTitle", "Configuration unavailable"),
          message,
        });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [apiRequest, showToast, t],
  );

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  /* =======================================================
     MARKET OPTIONS
     ======================================================= */

  const countryOptions = React.useMemo(() => {
    const codes = [
      ...new Set(
        marketRegions.map((region) => region.country_code).filter(Boolean),
      ),
    ].sort();

    return codes.map((code) => ({
      value: code,
      label: getCountryName(code, i18n.language),
    }));
  }, [marketRegions, i18n.language]);

  const regionOptions = React.useMemo(
    () =>
      marketRegions
        .filter(
          (region) => !countryFilter || region.country_code === countryFilter,
        )
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || "")),
        ),
    [marketRegions, countryFilter],
  );

  const selectedFormRegionIds = React.useMemo(
    () => normalizeRegionIds(form?.market_region_ids),
    [form?.market_region_ids],
  );

  const selectedFormRegionIdSet = React.useMemo(
    () => new Set(selectedFormRegionIds),
    [selectedFormRegionIds],
  );

  const formMarketRegions = React.useMemo(() => {
    return [...marketRegions]
      .filter(
        (region) =>
          region.is_active ||
          (modalMode === "EDIT" &&
            selectedFormRegionIdSet.has(Number(region.id))),
      )
      .sort((a, b) => {
        const countryCompare = String(a.country_code || "").localeCompare(
          String(b.country_code || ""),
        );

        if (countryCompare !== 0) return countryCompare;

        return String(a.name || "").localeCompare(String(b.name || ""));
      });
  }, [marketRegions, modalMode, selectedFormRegionIdSet]);

  const selectedFormMarketRegions = React.useMemo(
    () =>
      marketRegions.filter((region) =>
        selectedFormRegionIdSet.has(Number(region.id)),
      ),
    [marketRegions, selectedFormRegionIdSet],
  );

  /* =======================================================
     FILTERED DATA
     ======================================================= */

  const filteredFundingSources = React.useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return fundingSources.filter((source) => {
      const markets = getSourceMarkets(source);

      if (normalizedSearch) {
        const marketSearchText = markets.flatMap((market) => [
          market?.name,
          market?.code,
          market?.country_code,
          market?.country_code
            ? getCountryName(market.country_code, i18n.language)
            : "",
        ]);

        const text = [
          source.name,
          source.code,
          ...marketSearchText,
          source.category,
          source.allocation_role,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!text.includes(normalizedSearch)) {
          return false;
        }
      }

      if (
        countryFilter &&
        !markets.some((market) => market?.country_code === countryFilter)
      ) {
        return false;
      }

      if (
        regionFilter &&
        !markets.some((market) => String(market?.id) === String(regionFilter))
      ) {
        return false;
      }

      if (categoryFilter && source.category !== categoryFilter) {
        return false;
      }

      if (statusFilter === "ACTIVE" && !source.is_active) {
        return false;
      }

      if (statusFilter === "INACTIVE" && source.is_active) {
        return false;
      }

      return true;
    });
  }, [
    fundingSources,
    search,
    countryFilter,
    regionFilter,
    categoryFilter,
    statusFilter,
    i18n.language,
  ]);

  /* =======================================================
     SUMMARY
     ======================================================= */

  const summary = React.useMemo(
    () => ({
      total: fundingSources.length,
      active: fundingSources.filter((item) => item.is_active).length,
      lenderFinancing: fundingSources.filter(
        (item) => item.is_active && item.is_lender_financing,
      ).length,
      invoiceFunding: fundingSources.filter(
        (item) => item.is_active && item.can_fund_invoices,
      ).length,
    }),
    [fundingSources],
  );

  /* =======================================================
     DIRTY
     ======================================================= */

  const isDirty = React.useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialForm),
    [form, initialForm],
  );

  React.useEffect(() => {
    if (!modalOpen) {
      return undefined;
    }

    const handler = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      if (
        isDirty &&
        !window.confirm(
          t("modal.discardConfirm", "Discard your unsaved changes?"),
        )
      ) {
        return;
      }

      setModalOpen(false);
    };

    window.addEventListener("keydown", handler);

    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, [modalOpen, isDirty, t]);

  /* =======================================================
     FORM HELPERS
     ======================================================= */

  const setField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFormErrors((current) => ({
      ...current,
      [field]: "",
    }));
  };

  const handleNameChange = (value) => {
    setField("name", value);

    if (modalMode === "CREATE" && !codeTouched) {
      setForm((current) => ({
        ...current,
        name: value,
        code: normalizeCode(value),
      }));
    }
  };

  /* =======================================================
     OPEN CREATE
     ======================================================= */

  const openCreateModal = () => {
    const nextForm = {
      ...EMPTY_FORM,
      market_region_ids: regionFilter ? [Number(regionFilter)] : [],
    };

    setForm(nextForm);
    setInitialForm(nextForm);

    setCodeTouched(false);
    setFormErrors({});
    setModalMode("CREATE");
    setModalOpen(true);
  };

  /* =======================================================
     OPEN EDIT
     ======================================================= */

  const openEditModal = (source) => {
    const sourceMarkets = getSourceMarkets(source);

    const nextForm = {
      id: source.id,
      code: source.code || "",
      name: source.name || "",
      category: source.category || "LOAN",
      market_region_ids: normalizeRegionIds(
        sourceMarkets.map((market) => market.id),
      ),
      selectable_by_lender: Boolean(source.selectable_by_lender),
      is_lender_financing: Boolean(source.is_lender_financing),
      can_fund_invoices: Boolean(source.can_fund_invoices),
      allocation_role: source.allocation_role || "",
      allocation_rules: source.allocation_rules || {},
      is_active: Boolean(source.is_active),
      sort_order: source.sort_order ?? 1,
    };

    setForm(nextForm);
    setInitialForm(nextForm);

    setCodeTouched(true);
    setFormErrors({});
    setModalMode("EDIT");
    setModalOpen(true);
  };

  /* =======================================================
     CLOSE MODAL
     ======================================================= */

  const closeModal = () => {
    if (
      isDirty &&
      !window.confirm(
        t("modal.discardConfirm", "Discard your unsaved changes?"),
      )
    ) {
      return;
    }

    setModalOpen(false);
  };

  const toggleMarketRegion = (regionId) => {
    const normalizedId = Number(regionId);

    setForm((current) => {
      const currentIds = normalizeRegionIds(current?.market_region_ids);
      const exists = currentIds.includes(normalizedId);

      return {
        ...current,
        market_region_ids: exists
          ? currentIds.filter((id) => id !== normalizedId)
          : [...currentIds, normalizedId],
      };
    });

    setFormErrors((current) => ({
      ...current,
      market_region_ids: "",
    }));
  };

  /* =======================================================
     TOGGLES
     ======================================================= */

  const handleLenderFinancingChange = (checked) => {
    setForm((current) => ({
      ...current,
      is_lender_financing: checked,
      ...(checked
        ? {
            category: "LOAN",
            allocation_role: "LENDER",
            selectable_by_lender: true,
          }
        : {}),
    }));
  };

  const handleInvoiceFundingChange = (checked) => {
    setForm((current) => ({
      ...current,
      can_fund_invoices: checked,
      allocation_role: checked ? current.allocation_role || "LENDER" : "",
    }));
  };

  /* =======================================================
     VALIDATE
     ======================================================= */

  const validateForm = () => {
    const nextErrors = {};

    if (!form.name.trim()) {
      nextErrors.name = t(
        "validation.nameRequired",
        "Funding source name is required.",
      );
    }

    if (!form.code.trim()) {
      nextErrors.code = t(
        "validation.codeRequired",
        "Funding source code is required.",
      );
    }

    if (!normalizeRegionIds(form?.market_region_ids).length) {
      nextErrors.market_region_ids = t(
        "validation.marketRegionsRequired",
        "Select at least one market region.",
      );
    }

    if (!form.category) {
      nextErrors.category = t(
        "validation.categoryRequired",
        "Category is required.",
      );
    }

    if (form.can_fund_invoices && !form.allocation_role) {
      nextErrors.allocation_role = t(
        "validation.allocationRoleRequired",
        "Allocation role is required when this source can fund invoices.",
      );
    }

    if (form.is_lender_financing && form.category !== "LOAN") {
      nextErrors.category = t(
        "validation.lenderMustBeLoan",
        "A lender financing source must use the loan category.",
      );
    }

    setFormErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  /* =======================================================
     SAVE
     ======================================================= */

  const saveFundingSource = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setFormErrors({});

    const commonPayload = {
      name: form.name.trim(),

      category: form.category,

      market_region_ids: normalizeRegionIds(form?.market_region_ids),

      selectable_by_lender: form.selectable_by_lender,

      is_lender_financing: form.is_lender_financing,

      can_fund_invoices: form.can_fund_invoices,

      allocation_role: form.can_fund_invoices ? form.allocation_role : "",

      allocation_rules: form.allocation_rules || {},

      is_active: form.is_active,

      sort_order: Number(form.sort_order) || 1,
    };

    try {
      if (modalMode === "CREATE") {
        await apiRequest(FUNDING_SOURCES_URL, {
          method: "POST",
          body: JSON.stringify({
            ...commonPayload,
            code: normalizeCode(form.code),
          }),
        });

        showToast({
          type: "success",
          title: t("toast.createdTitle", "Funding source created"),
          message: t(
            "toast.created",
            "The funding source was created successfully.",
          ),
        });
      } else {
        const updatePayload = {
          ...commonPayload,
        };

        await apiRequest(`${FUNDING_SOURCES_URL}${form.id}/`, {
          method: "PATCH",
          body: JSON.stringify(updatePayload),
        });

        showToast({
          type: "success",
          title: t("toast.updatedTitle", "Funding source updated"),
          message: t(
            "toast.updated",
            "The funding source was updated successfully.",
          ),
        });
      }

      setModalOpen(false);

      await loadData({
        silent: true,
      });
    } catch (requestError) {
      const payloadErrors =
        requestError.payload?.errors || requestError.payload || null;

      if (payloadErrors && typeof payloadErrors === "object") {
        const normalizedErrors = {};

        Object.entries(payloadErrors).forEach(([field, value]) => {
          if (field === "detail") {
            return;
          }

          normalizedErrors[field] = Array.isArray(value)
            ? String(value[0] || "")
            : String(value || "");
        });

        setFormErrors(normalizedErrors);
      }

      showToast({
        type: "error",
        title: t("toast.saveErrorTitle", "Could not save funding source"),
        message:
          requestError?.message ||
          t("errors.save", "Could not save the funding source."),
      });
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     ACTIVATE / DEACTIVATE
     ======================================================= */

  const toggleActive = async (source) => {
    try {
      await apiRequest(`${FUNDING_SOURCES_URL}${source.id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          is_active: !source.is_active,
        }),
      });

      showToast({
        type: "success",
        title: source.is_active
          ? t("toast.deactivatedTitle", "Funding source deactivated")
          : t("toast.activatedTitle", "Funding source activated"),
        message: source.is_active
          ? t(
              "toast.deactivated",
              "The funding source was deactivated successfully.",
            )
          : t(
              "toast.activated",
              "The funding source was activated successfully.",
            ),
      });

      await loadData({
        silent: true,
      });
    } catch (requestError) {
      showToast({
        type: "error",
        title: t("toast.statusErrorTitle", "Could not update status"),
        message:
          requestError?.message ||
          t("errors.save", "Could not update the funding source."),
      });
    }
  };

  /* =======================================================
     RESET FILTERS
     ======================================================= */

  const resetFilters = () => {
    setSearch("");
    setCountryFilter("");
    setRegionFilter("");
    setCategoryFilter("");
    setStatusFilter("ALL");
  };

  const hasFilters = Boolean(
    search ||
    countryFilter ||
    regionFilter ||
    categoryFilter ||
    statusFilter !== "ALL",
  );

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <div className="afs-page">
      {/* ===================================================
          HEADER
          =================================================== */}

      <header className="afs-hero">
        <div className="afs-hero__content">
          <div className="afs-hero__eyebrow">
            <Settings2 size={15} strokeWidth={2} />
            {t("hero.eyebrow", "Platform configuration")}
          </div>

          <h1>{t("hero.title", "Funding Sources")}</h1>

          <p>
            {t(
              "hero.description",
              "Configure the financing, grant and subsidy sources available in each LEG-UP market.",
            )}
          </p>
        </div>

        <div className="afs-hero__actions">
          <button
            type="button"
            className="afs-btn afs-btn--secondary"
            onClick={() =>
              loadData({
                silent: true,
              })
            }
            disabled={refreshing}
          >
            <RefreshCw size={18} className={refreshing ? "is-spinning" : ""} />
            <span>{t("actions.refresh", "Refresh")}</span>
          </button>

          <button
            type="button"
            className="afs-btn afs-btn--primary"
            onClick={openCreateModal}
          >
            <Plus size={19} />
            <span>{t("actions.add", "Add funding source")}</span>
          </button>
        </div>

        <div className="afs-hero__visual">
          <div className="afs-hero__orb afs-hero__orb--one" />
          <div className="afs-hero__orb afs-hero__orb--two" />
          <Landmark size={118} strokeWidth={1} />
        </div>
      </header>

      {/* ===================================================
          SUMMARY
          =================================================== */}

      <section className="afs-summary">
        <SummaryCard
          icon={WalletCards}
          label={t("summary.total", "Funding sources")}
          value={summary.total}
          hint={t("summary.totalHint", "Configured across all LEG-UP markets")}
          delay={0}
        />

        <SummaryCard
          icon={ShieldCheck}
          label={t("summary.active", "Active")}
          value={summary.active}
          hint={t("summary.activeHint", "Currently available configurations")}
          delay={60}
        />

        <SummaryCard
          icon={Landmark}
          label={t("summary.lender", "Lender financing")}
          value={summary.lenderFinancing}
          hint={t(
            "summary.lenderHint",
            "Active lender-financing configurations",
          )}
          delay={120}
        />

        <SummaryCard
          icon={CircleDollarSign}
          label={t("summary.invoice", "Invoice funding")}
          value={summary.invoiceFunding}
          hint={t(
            "summary.invoiceHint",
            "Active sources that can fund invoices",
          )}
          delay={180}
        />
      </section>

      {/* ===================================================
          FILTERS
          =================================================== */}

      <section className="afs-panel afs-filters">
        <div className="afs-panel__heading">
          <div>
            <span className="afs-panel__eyebrow">
              <Filter size={14} />
              {t("filters.eyebrow", "Filters")}
            </span>

            <h2>{t("filters.title", "Find funding sources")}</h2>
          </div>

          {hasFilters ? (
            <button
              type="button"
              className="afs-text-button"
              onClick={resetFilters}
            >
              <X size={15} />
              {t("filters.clear", "Clear filters")}
            </button>
          ) : null}
        </div>

        <div className="afs-filter-grid">
          <label className="afs-field afs-field--search">
            <span className="afs-field__label">
              {t("filters.search", "Search")}
            </span>

            <span className="afs-input-wrap">
              <Search size={18} />

              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t(
                  "filters.searchPlaceholder",
                  "Search by source, code or market",
                )}
              />
            </span>
          </label>

          <label className="afs-field">
            <span className="afs-field__label">
              {t("fields.country", "Country")}
            </span>

            <span className="afs-select-wrap">
              <Globe2 size={17} />

              <select
                value={countryFilter}
                onChange={(event) => {
                  setCountryFilter(event.target.value);
                  setRegionFilter("");
                }}
              >
                <option value="">
                  {t("filters.allCountries", "All countries")}
                </option>

                {countryOptions.map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>

              <ChevronDown size={16} />
            </span>
          </label>

          <label className="afs-field">
            <span className="afs-field__label">
              {t("fields.marketRegion", "Market region")}
            </span>

            <span className="afs-select-wrap">
              <Building2 size={17} />

              <select
                value={regionFilter}
                onChange={(event) => setRegionFilter(event.target.value)}
              >
                <option value="">
                  {t("filters.allRegions", "All market regions")}
                </option>

                {regionOptions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                    {!region.is_active
                      ? ` — ${t("status.inactive", "Inactive")}`
                      : ""}
                  </option>
                ))}
              </select>

              <ChevronDown size={16} />
            </span>
          </label>

          <label className="afs-field">
            <span className="afs-field__label">
              {t("fields.category", "Category")}
            </span>

            <span className="afs-select-wrap">
              <Banknote size={17} />

              <select
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
              >
                <option value="">
                  {t("filters.allCategories", "All categories")}
                </option>

                {CATEGORY_VALUES.map((category) => (
                  <option key={category} value={category}>
                    {t(`categories.${category}`, category)}
                  </option>
                ))}
              </select>

              <ChevronDown size={16} />
            </span>
          </label>

          <label className="afs-field">
            <span className="afs-field__label">
              {t("fields.status", "Status")}
            </span>

            <span className="afs-select-wrap">
              <SlidersHorizontal size={17} />

              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="ALL">
                  {t("filters.allStatuses", "All statuses")}
                </option>

                <option value="ACTIVE">{t("status.active", "Active")}</option>

                <option value="INACTIVE">
                  {t("status.inactive", "Inactive")}
                </option>
              </select>

              <ChevronDown size={16} />
            </span>
          </label>
        </div>
      </section>

      {/* ===================================================
          LIST
          =================================================== */}

      <section className="afs-panel afs-list-panel">
        <div className="afs-panel__heading afs-panel__heading--list">
          <div>
            <span className="afs-panel__eyebrow">
              <FileSliders size={14} />
              {t("list.eyebrow", "Configuration")}
            </span>

            <h2>{t("list.title", "Configured funding sources")}</h2>

            <p>
              {t("list.count", "{{count}} funding sources", {
                count: filteredFundingSources.length,
              })}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="afs-state">
            <Loader2 size={34} className="is-spinning" />

            <strong>{t("states.loading", "Loading funding sources...")}</strong>
          </div>
        ) : error ? (
          <div className="afs-state afs-state--error">
            <X size={32} />

            <strong>
              {t("states.loadError", "Could not load funding sources")}
            </strong>

            <p>{error}</p>

            <button
              type="button"
              className="afs-btn afs-btn--secondary"
              onClick={() => loadData()}
            >
              <RefreshCw size={17} />
              {t("actions.tryAgain", "Try again")}
            </button>
          </div>
        ) : filteredFundingSources.length === 0 ? (
          <div className="afs-state afs-state--empty">
            <span className="afs-state__illustration">
              <BadgeEuro size={38} />
            </span>

            <strong>{t("states.emptyTitle", "No funding sources")}</strong>

            <p>
              {hasFilters
                ? t(
                    "states.emptyFiltered",
                    "No funding sources match the selected filters.",
                  )
                : t(
                    "states.emptyDescription",
                    "Create the first funding source configuration.",
                  )}
            </p>

            {!hasFilters ? (
              <button
                type="button"
                className="afs-btn afs-btn--primary"
                onClick={openCreateModal}
              >
                <Plus size={18} />
                {t("actions.add", "Add funding source")}
              </button>
            ) : null}
          </div>
        ) : (
          <>
            {/* DESKTOP TABLE */}

            <div className="afs-table-wrap">
              <table className="afs-table">
                <thead>
                  <tr>
                    <th>{t("table.source", "Source")}</th>
                    <th>{t("table.market", "Market")}</th>
                    <th>{t("table.category", "Category")}</th>
                    <th>{t("table.behaviour", "Behaviour")}</th>
                    <th>{t("table.status", "Status")}</th>
                    <th className="afs-table__actions-heading">
                      {t("table.actions", "Actions")}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredFundingSources.map((source, index) => {
                    const markets = source.market_regions || [];

                    if (
                      countryFilter &&
                      !markets.some(
                        (market) => market.country_code === countryFilter,
                      )
                    ) {
                      return false;
                    }

                    if (
                      regionFilter &&
                      !markets.some(
                        (market) => String(market.id) === String(regionFilter),
                      )
                    ) {
                      return false;
                    }

                    return (
                      <tr
                        key={source.id}
                        style={{
                          animationDelay: `${Math.min(index * 35, 280)}ms`,
                        }}
                      >
                        <td>
                          <div className="afs-source-cell">
                            <span className="afs-source-cell__icon">
                              {source.is_lender_financing ? (
                                <Landmark size={20} />
                              ) : (
                                <WalletCards size={20} />
                              )}
                            </span>

                            <div>
                              <strong>{source.name}</strong>
                              <code>{source.code}</code>
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="afs-market">
                            {markets.length ? (
                              markets.map((market) => (
                                <span key={market.id}>
                                  <Building2 size={14} />
                                  {marketRegionLabel(market, i18n.language)}
                                </span>
                              ))
                            ) : (
                              <span>
                                <Globe2 size={14} />—
                              </span>
                            )}
                          </div>
                        </td>

                        <td>
                          <span className="afs-category-badge">
                            {t(
                              `categories.${source.category}`,
                              source.category,
                            )}
                          </span>
                        </td>

                        <td>
                          <div className="afs-behaviour">
                            {source.is_lender_financing ? (
                              <span className="afs-pill afs-pill--accent">
                                <Landmark size={13} />
                                {t(
                                  "badges.lenderFinancing",
                                  "Lender financing",
                                )}
                              </span>
                            ) : null}

                            {source.can_fund_invoices ? (
                              <span className="afs-pill">
                                <CircleDollarSign size={13} />
                                {t("badges.invoiceFunding", "Invoice funding")}
                              </span>
                            ) : null}

                            {source.selectable_by_lender ? (
                              <span className="afs-pill">
                                <Check size={13} />
                                {t(
                                  "badges.lenderSelectable",
                                  "Selectable by lender",
                                )}
                              </span>
                            ) : null}

                            {source.allocation_role ? (
                              <span className="afs-behaviour__role">
                                {t("table.allocationRole", "Allocation role")}{" "}
                                <strong>
                                  {t(
                                    `roles.${source.allocation_role}`,
                                    source.allocation_role,
                                  )}
                                </strong>
                              </span>
                            ) : null}
                          </div>
                        </td>

                        <td>
                          <StatusBadge
                            active={source.is_active}
                            activeLabel={t("status.active", "Active")}
                            inactiveLabel={t("status.inactive", "Inactive")}
                          />
                        </td>

                        <td>
                          <div className="afs-row-actions">
                            <button
                              type="button"
                              className="afs-icon-button"
                              onClick={() => openEditModal(source)}
                              title={t("actions.edit", "Edit")}
                            >
                              <Edit3 size={17} />
                            </button>

                            <button
                              type="button"
                              className={`afs-icon-button ${
                                source.is_active
                                  ? "afs-icon-button--danger"
                                  : "afs-icon-button--success"
                              }`}
                              onClick={() => toggleActive(source)}
                              title={
                                source.is_active
                                  ? t("actions.deactivate", "Deactivate")
                                  : t("actions.activate", "Activate")
                              }
                            >
                              {source.is_active ? (
                                <ToggleRight size={19} />
                              ) : (
                                <ToggleLeft size={19} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* MOBILE CARDS */}

            <div className="afs-mobile-list">
              {filteredFundingSources.map((source, index) => {
                const markets = getSourceMarkets(source);

                return (
                  <article
                    key={source.id}
                    className="afs-mobile-card"
                    style={{
                      animationDelay: `${Math.min(index * 45, 300)}ms`,
                    }}
                  >
                    <div className="afs-mobile-card__header">
                      <div className="afs-source-cell">
                        <span className="afs-source-cell__icon">
                          {source.is_lender_financing ? (
                            <Landmark size={20} />
                          ) : (
                            <WalletCards size={20} />
                          )}
                        </span>

                        <div>
                          <strong>{source.name}</strong>
                          <code>{source.code}</code>
                        </div>
                      </div>

                      <StatusBadge
                        active={source.is_active}
                        activeLabel={t("status.active", "Active")}
                        inactiveLabel={t("status.inactive", "Inactive")}
                      />
                    </div>

                    <div className="afs-mobile-card__market">
                      {markets.length ? (
                        markets.map((market) => (
                          <span key={market.id}>
                            <Building2 size={15} />
                            {marketRegionLabel(market, i18n.language)}
                          </span>
                        ))
                      ) : (
                        <span>
                          <Globe2 size={15} />—
                        </span>
                      )}
                    </div>

                    <div className="afs-mobile-card__badges">
                      <span className="afs-category-badge">
                        {t(`categories.${source.category}`, source.category)}
                      </span>

                      {source.is_lender_financing ? (
                        <span className="afs-pill afs-pill--accent">
                          {t("badges.lenderFinancing", "Lender financing")}
                        </span>
                      ) : null}

                      {source.can_fund_invoices ? (
                        <span className="afs-pill">
                          {t("badges.invoiceFunding", "Invoice funding")}
                        </span>
                      ) : null}
                    </div>

                    {source.allocation_role ? (
                      <div className="afs-mobile-card__role">
                        <span>
                          {t("table.allocationRole", "Allocation role")}
                        </span>

                        <strong>
                          {t(
                            `roles.${source.allocation_role}`,
                            source.allocation_role,
                          )}
                        </strong>
                      </div>
                    ) : null}

                    <div className="afs-mobile-card__actions">
                      <button
                        type="button"
                        className="afs-btn afs-btn--secondary"
                        onClick={() => openEditModal(source)}
                      >
                        <Edit3 size={16} />
                        {t("actions.edit", "Edit")}
                      </button>

                      <button
                        type="button"
                        className={`afs-btn ${
                          source.is_active
                            ? "afs-btn--danger-soft"
                            : "afs-btn--success-soft"
                        }`}
                        onClick={() => toggleActive(source)}
                      >
                        {source.is_active ? (
                          <ToggleRight size={17} />
                        ) : (
                          <ToggleLeft size={17} />
                        )}

                        {source.is_active
                          ? t("actions.deactivate", "Deactivate")
                          : t("actions.activate", "Activate")}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </section>

      {/* ===================================================
          MODAL
          =================================================== */}

      {modalOpen ? (
        <div
          className="afs-modal-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeModal();
            }
          }}
        >
          <section
            className="afs-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="afs-modal-title"
          >
            <header className="afs-modal__header">
              <div>
                <span className="afs-panel__eyebrow">
                  <SlidersHorizontal size={14} />

                  {modalMode === "CREATE"
                    ? t("modal.createEyebrow", "New configuration")
                    : t("modal.editEyebrow", "Edit configuration")}
                </span>

                <h2 id="afs-modal-title">
                  {modalMode === "CREATE"
                    ? t("modal.createTitle", "Add funding source")
                    : t("modal.editTitle", "Edit funding source")}
                </h2>

                <p>
                  {t(
                    "modal.description",
                    "Configure how this funding source is used across one or more LEG-UP markets.",
                  )}
                </p>
              </div>

              <button
                type="button"
                className="afs-modal__close"
                onClick={closeModal}
                aria-label={t("actions.close", "Close")}
              >
                <X size={21} />
              </button>
            </header>

            <div className="afs-modal__body">
              {/* -------------------------------------------
                  IDENTITY
                  ------------------------------------------- */}

              <div className="afs-form-section">
                <div className="afs-form-section__heading">
                  <span className="afs-form-section__icon">
                    <WalletCards size={18} />
                  </span>

                  <div>
                    <h3>{t("sections.identity.title", "Funding source")}</h3>

                    <p>
                      {t(
                        "sections.identity.description",
                        "Define the source name, stable code and financing category.",
                      )}
                    </p>
                  </div>
                </div>

                <div className="afs-form-grid">
                  <label className="afs-field afs-field--wide">
                    <span className="afs-field__label">
                      {t("fields.name", "Name")}
                      <em>*</em>
                    </span>

                    <input
                      type="text"
                      value={form.name}
                      onChange={(event) => handleNameChange(event.target.value)}
                      placeholder={t(
                        "fields.namePlaceholder",
                        "e.g. Mijn Verbouwlening",
                      )}
                      className={formErrors.name ? "has-error" : ""}
                    />

                    {formErrors.name ? (
                      <small className="afs-field__error">
                        {formErrors.name}
                      </small>
                    ) : null}
                  </label>

                  <label className="afs-field">
                    <span className="afs-field__label">
                      {t("fields.code", "Code")}
                      <em>*</em>
                    </span>

                    <input
                      type="text"
                      value={form.code}
                      disabled={modalMode === "EDIT"}
                      onChange={(event) => {
                        setCodeTouched(true);
                        setField("code", normalizeCode(event.target.value));
                      }}
                      placeholder="SOURCE_CODE"
                      className={formErrors.code ? "has-error" : ""}
                    />

                    <small className="afs-field__hint">
                      {modalMode === "EDIT"
                        ? t(
                            "fields.codeLockedHint",
                            "The code is a stable identifier and cannot be changed.",
                          )
                        : t(
                            "fields.codeHint",
                            "A stable code is generated from the name and can be adjusted before creation.",
                          )}
                    </small>

                    {formErrors.code ? (
                      <small className="afs-field__error">
                        {formErrors.code}
                      </small>
                    ) : null}
                  </label>

                  <label className="afs-field">
                    <span className="afs-field__label">
                      {t("fields.category", "Category")}
                      <em>*</em>
                    </span>

                    <span className="afs-select-wrap">
                      <Banknote size={17} />

                      <select
                        value={form.category}
                        disabled={form.is_lender_financing}
                        onChange={(event) =>
                          setField("category", event.target.value)
                        }
                        className={formErrors.category ? "has-error" : ""}
                      >
                        {CATEGORY_VALUES.map((category) => (
                          <option key={category} value={category}>
                            {t(`categories.${category}`, category)}
                          </option>
                        ))}
                      </select>

                      <ChevronDown size={16} />
                    </span>

                    {formErrors.category ? (
                      <small className="afs-field__error">
                        {formErrors.category}
                      </small>
                    ) : null}
                  </label>
                </div>
              </div>

              {/* -------------------------------------------
                  MARKET REGIONS
                  ------------------------------------------- */}

              <div className="afs-form-section">
                <div className="afs-form-section__heading">
                  <span className="afs-form-section__icon">
                    <Globe2 size={18} />
                  </span>

                  <div>
                    <h3>{t("sections.market.title", "Market regions")}</h3>

                    <p>
                      {t(
                        "sections.market.description",
                        "Select every LEG-UP market region in which this funding source is available. A source may apply to regions in different countries.",
                      )}
                    </p>
                  </div>
                </div>

                <div className="afs-field afs-field--wide">
                  <span className="afs-field__label">
                    {t("fields.marketRegions", "Market regions")}
                    <em>*</em>
                  </span>

                  <div className="afs-toggle-grid">
                    {formMarketRegions.length ? (
                      formMarketRegions.map((region) => {
                        const regionId = Number(region.id);
                        const selected = selectedFormRegionIdSet.has(regionId);
                        const disabled = !region.is_active && !selected;

                        return (
                          <Toggle
                            key={region.id}
                            checked={selected}
                            disabled={disabled}
                            onChange={() => toggleMarketRegion(regionId)}
                            label={marketRegionLabel(region, i18n.language)}
                            description={[
                              region.code || null,
                              !region.is_active
                                ? t("status.inactive", "Inactive")
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          />
                        );
                      })
                    ) : (
                      <div className="afs-info-box">
                        <Globe2 size={18} />
                        <p>
                          {t(
                            "fields.noMarketRegions",
                            "No market regions are available.",
                          )}
                        </p>
                      </div>
                    )}
                  </div>

                  <small className="afs-field__hint">
                    {t(
                      "fields.marketRegionsHint",
                      "Select one or more regions. Active funding sources can span multiple regions and multiple countries.",
                    )}
                  </small>

                  {formErrors.market_region_ids ? (
                    <small className="afs-field__error">
                      {formErrors.market_region_ids}
                    </small>
                  ) : null}
                </div>

                {selectedFormMarketRegions.length ? (
                  <div className="afs-market-preview">
                    <Globe2 size={17} />

                    <span>{t("market.appliesTo", "Applies to")}</span>

                    <strong>
                      {t(
                        "market.selectedCount",
                        "{{count}} market region(s) selected",
                        { count: selectedFormMarketRegions.length },
                      )}
                    </strong>

                    <div className="afs-behaviour">
                      {selectedFormMarketRegions.map((region) => (
                        <span key={region.id} className="afs-pill">
                          <Building2 size={13} />
                          {marketRegionLabel(region, i18n.language)}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* -------------------------------------------
                  FINANCING PLAN
                  ------------------------------------------- */}

              <div className="afs-form-section">
                <div className="afs-form-section__heading">
                  <span className="afs-form-section__icon">
                    <Landmark size={18} />
                  </span>

                  <div>
                    <h3>
                      {t("sections.financing.title", "Financing behaviour")}
                    </h3>

                    <p>
                      {t(
                        "sections.financing.description",
                        "Control whether lenders can select this source and whether it represents the lender-financing option in the selected market regions.",
                      )}
                    </p>
                  </div>
                </div>

                <div className="afs-toggle-grid">
                  <Toggle
                    checked={form.selectable_by_lender}
                    onChange={(value) =>
                      setField("selectable_by_lender", value)
                    }
                    label={t(
                      "toggles.selectable.title",
                      "Selectable by lender",
                    )}
                    description={t(
                      "toggles.selectable.description",
                      "Allow lender users to select this funding source when the workflow permits it.",
                    )}
                  />

                  <Toggle
                    checked={form.is_lender_financing}
                    onChange={handleLenderFinancingChange}
                    label={t(
                      "toggles.lenderFinancing.title",
                      "Lender financing source",
                    )}
                    description={t(
                      "toggles.lenderFinancing.description",
                      "Marks this as the active lender-financing configuration for the selected market regions.",
                    )}
                  />
                </div>

                {form.is_lender_financing ? (
                  <div className="afs-info-box">
                    <ShieldCheck size={18} />

                    <p>
                      {t(
                        "toggles.lenderFinancing.info",
                        "Only one active lender-financing funding-source type can exist in each market region. It must use the LOAN category and LENDER allocation role.",
                      )}
                    </p>
                  </div>
                ) : null}
              </div>

              {/* -------------------------------------------
                  ALLOCATION
                  ------------------------------------------- */}

              <div className="afs-form-section">
                <div className="afs-form-section__heading">
                  <span className="afs-form-section__icon">
                    <CircleDollarSign size={18} />
                  </span>

                  <div>
                    <h3>
                      {t("sections.allocation.title", "Invoice allocation")}
                    </h3>

                    <p>
                      {t(
                        "sections.allocation.description",
                        "Define whether this source can fund renovation invoices and which actor manages allocations.",
                      )}
                    </p>
                  </div>
                </div>

                <div className="afs-toggle-grid afs-toggle-grid--single">
                  <Toggle
                    checked={form.can_fund_invoices}
                    onChange={handleInvoiceFundingChange}
                    label={t(
                      "toggles.invoiceFunding.title",
                      "Can fund invoices",
                    )}
                    description={t(
                      "toggles.invoiceFunding.description",
                      "Allow renovation invoice amounts to be allocated to this funding source.",
                    )}
                  />
                </div>

                {form.can_fund_invoices ? (
                  <div className="afs-form-grid afs-form-grid--allocation">
                    <label className="afs-field">
                      <span className="afs-field__label">
                        {t("fields.allocationRole", "Allocation role")}
                        <em>*</em>
                      </span>

                      <span className="afs-select-wrap">
                        <ShieldCheck size={17} />

                        <select
                          value={form.allocation_role}
                          disabled={form.is_lender_financing}
                          onChange={(event) =>
                            setField("allocation_role", event.target.value)
                          }
                          className={
                            formErrors.allocation_role ? "has-error" : ""
                          }
                        >
                          <option value="">
                            {t("fields.chooseRole", "Choose role")}
                          </option>

                          {ALLOCATION_ROLE_VALUES.map((role) => (
                            <option key={role} value={role}>
                              {t(`roles.${role}`, role)}
                            </option>
                          ))}
                        </select>

                        <ChevronDown size={16} />
                      </span>

                      <small className="afs-field__hint">
                        {t(
                          "fields.allocationRoleHint",
                          "The actor responsible for assigning invoice amounts to this source.",
                        )}
                      </small>

                      {formErrors.allocation_role ? (
                        <small className="afs-field__error">
                          {formErrors.allocation_role}
                        </small>
                      ) : null}
                    </label>
                  </div>
                ) : null}
              </div>

              {/* -------------------------------------------
                  AVAILABILITY
                  ------------------------------------------- */}

              <div className="afs-form-section">
                <div className="afs-form-section__heading">
                  <span className="afs-form-section__icon">
                    <Settings2 size={18} />
                  </span>

                  <div>
                    <h3>{t("sections.availability.title", "Availability")}</h3>

                    <p>
                      {t(
                        "sections.availability.description",
                        "Control display order and whether this configuration is currently active.",
                      )}
                    </p>
                  </div>
                </div>

                <div className="afs-form-grid">
                  <label className="afs-field">
                    <span className="afs-field__label">
                      {t("fields.sortOrder", "Sort order")}
                    </span>

                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={form.sort_order}
                      onChange={(event) =>
                        setField("sort_order", event.target.value)
                      }
                    />

                    <small className="afs-field__hint">
                      {t("fields.sortOrderHint", "Lower values appear first.")}
                    </small>
                  </label>

                  <div className="afs-field afs-field--toggle-field">
                    <span className="afs-field__label">
                      {t("fields.status", "Status")}
                    </span>

                    <Toggle
                      checked={form.is_active}
                      onChange={(value) => setField("is_active", value)}
                      label={
                        form.is_active
                          ? t("status.active", "Active")
                          : t("status.inactive", "Inactive")
                      }
                      description={
                        form.is_active
                          ? t(
                              "toggles.active.activeDescription",
                              "This funding source is available for the selected market regions.",
                            )
                          : t(
                              "toggles.active.inactiveDescription",
                              "This funding source is retained but unavailable for new use.",
                            )
                      }
                    />
                  </div>
                </div>
              </div>

              {formErrors.non_field_errors ? (
                <div className="afs-form-error">
                  <X size={18} />
                  {formErrors.non_field_errors}
                </div>
              ) : null}
            </div>

            <footer className="afs-modal__footer">
              <div className="afs-modal__footer-status">
                {isDirty ? (
                  <>
                    <span className="afs-unsaved-dot" />
                    {t("modal.unsaved", "Unsaved changes")}
                  </>
                ) : (
                  <>
                    <Check size={15} />
                    {t("modal.noChanges", "No changes")}
                  </>
                )}
              </div>

              <div className="afs-modal__footer-actions">
                <button
                  type="button"
                  className="afs-btn afs-btn--secondary"
                  onClick={closeModal}
                  disabled={saving}
                >
                  {t("actions.cancel", "Cancel")}
                </button>

                <button
                  type="button"
                  className="afs-btn afs-btn--primary"
                  onClick={saveFundingSource}
                  disabled={saving || (modalMode === "EDIT" && !isDirty)}
                >
                  {saving ? (
                    <Loader2 size={18} className="is-spinning" />
                  ) : (
                    <Check size={18} />
                  )}

                  {saving
                    ? t("actions.saving", "Saving...")
                    : modalMode === "CREATE"
                      ? t("actions.create", "Create funding source")
                      : t("actions.save", "Save changes")}
                </button>
              </div>
            </footer>
          </section>
        </div>
      ) : null}
    </div>
  );
}
